// ── Invoice PDF Generator ─────────────────────────────────────────────────────
// Uses a hidden off-screen iframe that loads tax_invoice.html with ?autoDownload=1.
// tax_invoice.html reads from localStorage, populates the form, and triggers
// the existing html2pdf download — giving 100% pixel-perfect PDF output.

// ── Formatting helpers (used by CreateBilling for live totals) ────────────────
export function fmtINR(n) {
  const num = parseFloat(n) || 0;
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const _ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine',
  'Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
const _tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];

function _numToWords(n) {
  n = Math.floor(n);
  if (n === 0) return 'Zero';
  let s = '';
  if (Math.floor(n / 10000000) > 0) { s += _numToWords(Math.floor(n / 10000000)) + ' Crore '; n %= 10000000; }
  if (Math.floor(n / 100000) > 0)   { s += _numToWords(Math.floor(n / 100000)) + ' Lakh ';   n %= 100000; }
  if (Math.floor(n / 1000) > 0)     { s += _numToWords(Math.floor(n / 1000)) + ' Thousand '; n %= 1000; }
  if (Math.floor(n / 100) > 0)      { s += _numToWords(Math.floor(n / 100)) + ' Hundred ';   n %= 100; }
  if (n > 0) {
    if (n < 20) s += _ones[n] + ' ';
    else s += _tens[Math.floor(n / 10)] + (n % 10 ? ' ' + _ones[n % 10] : '') + ' ';
  }
  return s.trim();
}

export function toINRWords(amount) {
  const total = Math.round(amount * 100) / 100;
  const rupees = Math.floor(total);
  const paise = Math.round((total - rupees) * 100);
  let words = 'INR ' + _numToWords(rupees);
  if (paise > 0) words += ' and ' + _numToWords(paise) + ' Paise';
  return words + ' only';
}

// ── Convert new invoiceData format → legacy format for tax_invoice.html ───────
function _toLegacy(invoiceData) {
  const items = Array.isArray(invoiceData.items) ? invoiceData.items : [];
  return {
    meta: {
      invoiceNo:        invoiceData.invoiceNo,
      invoiceDate:      invoiceData.invoiceDate,
      deliveryNote:     invoiceData.invoiceNo,
      refNo:            invoiceData.refNo || '',
      buyerOrderNo:     invoiceData.buyerOrderNo || ('S-' + invoiceData.invoiceNo),
      buyerOrderDate:   invoiceData.invoiceDate,
      deliveryNoteDate: invoiceData.invoiceDate,
      dispatchDocNo:    invoiceData.invoiceNo,
    },
    customer: {
      name:    invoiceData.customerName,
      phone:   invoiceData.phone,
      address: invoiceData.customerAddress,
      orders:  items.map(it => ({
        vehicle_no:     it.description,
        total_qty:      parseFloat(it.qty)  || 0,
        price_per_unit: parseFloat(it.rate) || 0,
        total_amount:   (parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0),
      })),
    },
    invoiceData, // also keep new format so BillingHistory re-download works
  };
}

// ── Main: generate + auto-download PDF via hidden off-screen iframe ───────────
export function downloadInvoicePDF(invoiceData) {
  return new Promise((resolve, reject) => {
    const key = `inv_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

    // Persist for tax_invoice.html to read
    localStorage.setItem(key, JSON.stringify(_toLegacy(invoiceData)));

    // Off-screen iframe — browser still fully renders it, html2pdf inside it works normally
    const iframe = document.createElement('iframe');
    iframe.title = 'Invoice Generator';
    iframe.style.cssText =
      'position:fixed;left:-9999px;top:0;width:960px;height:1500px;border:none;z-index:-1;';

    const timers = [];
    let done = false;

    const finish = (err) => {
      if (done) return;
      done = true;
      timers.forEach(clearTimeout);
      try { document.body.removeChild(iframe); } catch (_) {}
      if (err) reject(err); else resolve();
    };

    iframe.onload = () => {
      // tax_invoice.html auto-triggers downloadPDF 700 ms after load.
      // html2pdf takes ~1–5 s to produce the file.
      // Clean up the iframe after 9 s; the actual PDF download runs independently.
      timers.push(setTimeout(() => finish(null), 9000));
    };

    iframe.onerror = () => finish(new Error('Failed to load invoice template'));

    // Hard timeout fallback
    timers.push(setTimeout(() => finish(null), 14000));

    document.body.appendChild(iframe);
    iframe.src = `/tax_invoice.html?invoiceKey=${key}&autoDownload=1`;
  });
}
